import { AlertCircle, RefreshCw } from 'lucide-react';
import { Component, ErrorInfo, ReactNode, Suspense, useEffect, useState } from 'react';

import { useChunkErrorRecovery } from '@/components/error-handling/hooks/use-chunk-error-recovery';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import type { AsyncBoundaryProps } from '../types/component-boundary-types';
import { BOUNDARY_LEVEL } from '../types/component-boundary-enums';

// Types for different boundary contexts
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

// Error boundary class component
class AsyncErrorBoundary extends Component<
  AsyncBoundaryProps & { onErrorCapture: (error: Error, errorInfo: ErrorInfo) => void },
  ErrorBoundaryState
> {
  private resetTimeoutId: number | null = null;

  constructor(
    props: AsyncBoundaryProps & { onErrorCapture: (error: Error, errorInfo: ErrorInfo) => void }
  ) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('AsyncBoundary caught an error:', error, errorInfo);

    this.setState({ errorInfo });
    this.props.onErrorCapture(error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  componentDidUpdate(
    prevProps: AsyncBoundaryProps & { onErrorCapture: (error: Error, errorInfo: ErrorInfo) => void }
  ) {
    const { resetKeys } = this.props;
    const prevResetKeys = prevProps.resetKeys;

    if (this.state.hasError && prevResetKeys !== resetKeys) {
      if (resetKeys?.some((key, idx) => prevResetKeys?.[idx] !== key)) {
        this.reset();
      }
    }
  }

  componentWillUnmount(): void {
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId);
    }
  }

  reset = (): void => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    this.props.onReset?.();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.errorFallback) {
        return this.props.errorFallback;
      }

      return (
        <DefaultErrorFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onReset={this.reset}
          level={this.props.level}
          className={this.props.className}
          errorTitle={this.props.errorTitle}
          errorDescription={this.props.errorDescription}
          showErrorDetails={this.props.showErrorDetails}
          retryable={this.props.retryable}
        />
      );
    }

    return this.props.children;
  }
}

// Default loading fallbacks for different levels
const DefaultLoadingFallback = ({ level }: { level?: BOUNDARY_LEVEL }) => {
  switch (level) {
    case BOUNDARY_LEVEL.PAGE:
      return (
        <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4">
          <div className="flex flex-col items-center gap-4">
            <Spinner type="gradient" className="size-12" />
            <p className="text-lg text-foreground/70">Loading page...</p>
          </div>
        </div>
      );

    case BOUNDARY_LEVEL.CHART:
    case BOUNDARY_LEVEL.WIDGET:
      return (
        <Card className="flex h-full w-full flex-col p-4">
          <CardContent className="h-full p-0">
            <div className="flex h-full w-full flex-col items-center justify-center">
              <Spinner type="gradient" className="size-8" />
              <p className="mt-2 text-sm text-foreground/70">Loading {level}...</p>
            </div>
          </CardContent>
        </Card>
      );

    case BOUNDARY_LEVEL.COMPONENT:
    default:
      return (
        <div className="flex h-full w-full items-center justify-center p-4">
          <div className="flex flex-col items-center gap-2">
            <Spinner type="gradient" className="size-6" />
            <p className="text-sm text-foreground/70">Loading...</p>
          </div>
        </div>
      );
  }
};

// Default error fallbacks for different levels
const DefaultErrorFallback = ({
  error,
  errorInfo,
  onReset,
  level = BOUNDARY_LEVEL.COMPONENT,
  className,
  errorTitle,
  errorDescription,
  showErrorDetails = false,
  retryable = true,
}: {
  error: Error | null;
  errorInfo: ErrorInfo | null;
  onReset: () => void;
  level?: BOUNDARY_LEVEL;
  className?: string;
  errorTitle?: string;
  errorDescription?: string;
  showErrorDetails?: boolean;
  retryable?: boolean;
}) => {
  // Call all hooks before any early return to satisfy Rules of Hooks.
  const isChunkRecovering = useChunkErrorRecovery(error);
  const [isRetrying, setIsRetrying] = useState(false);

  // Chunk-load failures are recovery actions, not user-facing faults — never
  // display alarming copy. Render the same neutral loading fallback that
  // Suspense uses for this level so layout stays stable during the reload.
  if (isChunkRecovering) {
    return <DefaultLoadingFallback level={level} />;
  }

  const handleRetry = () => {
    setIsRetrying(true);

    setTimeout(() => {
      setIsRetrying(false);
      onReset();
    }, 500);
  };

  const getDefaultTitle = () => {
    switch (level) {
      case BOUNDARY_LEVEL.PAGE:
        return 'Page failed to load';
      case BOUNDARY_LEVEL.CHART:
        return 'Chart failed to load';
      case BOUNDARY_LEVEL.WIDGET:
        return 'Widget failed to load';
      case BOUNDARY_LEVEL.COMPONENT:
      default:
        return 'Component failed to load';
    }
  };

  const getDefaultDescription = () => {
    switch (level) {
      case BOUNDARY_LEVEL.PAGE:
        return 'There was an error loading this page. Please try refreshing or contact support if the problem persists.';
      case BOUNDARY_LEVEL.CHART:
      case BOUNDARY_LEVEL.WIDGET:
        return 'Unable to load the data for this component. This may be due to network issues or a temporary server problem.';
      case BOUNDARY_LEVEL.COMPONENT:
      default:
        return 'An error occurred while loading this component. Please try again.';
    }
  };

  const title = errorTitle || getDefaultTitle();
  const description = errorDescription || getDefaultDescription();

  if (level === BOUNDARY_LEVEL.PAGE) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4">
        <div className="w-full max-w-md text-center">
          <div className="mb-8 flex justify-center">
            <div className="rounded-full bg-destructive/10 p-3">
              <AlertCircle className="h-8 w-8 text-destructive" />
            </div>
          </div>

          <h1 className="mb-2 text-xl font-semibold text-foreground">{title}</h1>
          <p className="mb-8 text-foreground/80">{description}</p>

          {showErrorDetails && error && (
            <div className="mb-6 rounded-lg border border-destructive/20 bg-destructive/10 p-4 text-left">
              <p className="text-sm font-semibold text-foreground">Error Details:</p>
              <p className="mt-1 text-sm text-foreground/80">{error.message}</p>
              {import.meta.env.DEV && errorInfo && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-sm text-foreground/80">Stack Trace</summary>
                  <pre className="mt-2 overflow-auto text-sm text-muted-foreground">
                    {errorInfo.componentStack}
                  </pre>
                </details>
              )}
            </div>
          )}

          <div className="space-y-4">
            {retryable && (
              <Button
                onClick={handleRetry}
                disabled={isRetrying}
                className="flex w-full items-center justify-center gap-2"
              >
                <RefreshCw className={cn('h-4 w-4', isRetrying && 'animate-spin')} />
                {isRetrying ? 'Retrying...' : 'Try Again'}
              </Button>
            )}

            <Button variant="outline" onClick={() => window.location.reload()} className="w-full">
              Reload Page
            </Button>
          </div>

          {error && (
            <p className="mt-4 text-sm text-muted-foreground">
              Error Reference: {error.message || 'Unknown error'}
            </p>
          )}
        </div>
      </div>
    );
  }

  if (level === BOUNDARY_LEVEL.CHART || level === BOUNDARY_LEVEL.WIDGET) {
    return (
      <Card className={cn('flex h-full w-full flex-col p-4', className)}>
        <CardContent className="h-full p-0">
          <div className="flex h-full w-full flex-col items-center justify-center">
            <div className="mb-4 rounded-full bg-destructive/10 p-2">
              <AlertCircle className="h-6 w-6 text-destructive" />
            </div>

            <h3 className="mb-2 text-base font-semibold text-foreground">{title}</h3>
            <p className="mb-4 max-w-sm text-center text-sm text-foreground/80">{description}</p>

            {showErrorDetails && error && (
              <p className="mb-4 text-center text-sm text-foreground/80">{error.message}</p>
            )}

            {retryable && (
              <Button
                size="sm"
                onClick={handleRetry}
                disabled={isRetrying}
                className="flex items-center gap-2"
              >
                <RefreshCw className={cn('h-3 w-3', isRetrying && 'animate-spin')} />
                {isRetrying ? 'Retrying...' : 'Retry'}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className={cn('flex h-full w-full flex-col items-center justify-center p-4', className)}>
      <div className="mb-3 rounded-full bg-destructive/10 p-2">
        <AlertCircle className="h-5 w-5 text-destructive" />
      </div>

      <h3 className="mb-2 text-sm font-semibold text-foreground">{title}</h3>
      <p className="mb-4 max-w-xs text-center text-sm text-foreground/80">{description}</p>

      {showErrorDetails && error && (
        <p className="mb-3 text-center text-sm text-foreground/80">{error.message}</p>
      )}

      {retryable && (
        <Button
          size="sm"
          variant="outline"
          onClick={handleRetry}
          disabled={isRetrying}
          className="flex items-center gap-2 text-sm"
        >
          <RefreshCw className={cn('h-3 w-3', isRetrying && 'animate-spin')} />
          {isRetrying ? 'Retrying...' : 'Retry'}
        </Button>
      )}
    </div>
  );
};

// Main AsyncBoundary component
const AsyncBoundary = ({
  children,
  level = BOUNDARY_LEVEL.COMPONENT,
  className,
  loadingFallback,
  errorFallback,
  errorTitle,
  errorDescription,
  onError,
  resetKeys,
  onReset,
  showErrorDetails = import.meta.env.DEV, // Show details in development by default
  retryable = true,
}: AsyncBoundaryProps) => {
  const [boundaryKey, setBoundaryKey] = useState(0);

  const handleErrorCapture = (error: Error, errorInfo: ErrorInfo) => {
    // Log error for monitoring/analytics
    console.error('AsyncBoundary Error:', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      level,
    });
  };

  const handleReset = () => {
    setBoundaryKey(prev => prev + 1);
    onReset?.();
  };

  // Reset boundary when resetKeys change
  useEffect(() => {
    if (resetKeys && resetKeys.length > 0) {
      setBoundaryKey(prev => prev + 1);
    }
  }, [resetKeys]);

  const fallback = loadingFallback || <DefaultLoadingFallback level={level} />;

  return (
    <AsyncErrorBoundary
      key={boundaryKey}
      level={level}
      className={className}
      errorFallback={errorFallback}
      errorTitle={errorTitle}
      errorDescription={errorDescription}
      onError={onError}
      onErrorCapture={handleErrorCapture}
      onReset={handleReset}
      showErrorDetails={showErrorDetails}
      retryable={retryable}
      resetKeys={resetKeys}
    >
      <Suspense fallback={fallback}>{children}</Suspense>
    </AsyncErrorBoundary>
  );
};

export function ComponentBoundary(props: Omit<AsyncBoundaryProps, 'level'>) {
  return <AsyncBoundary {...props} level={BOUNDARY_LEVEL.COMPONENT} />;

}
